import React, { useCallback, useEffect, useState } from 'react';
import api from '../api/axios';
import Pagination from './Pagination';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import { useConfirm } from '../context/ConfirmContext';
import { useToast } from '../context/ToastContext';

// Products taken off the shop with "Remove". They're hidden from customers but kept, because
// curated carts, quotes and orders read their name and price live. From here staff can put one
// back (Restore) or, if nothing has ever used it — an accidental duplicate, say — delete it for good.

const btn = { padding: '6px 12px', background: '#fff', color: '#374151', border: '1.5px solid #E5E7EB', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' };
const btnPrimary = { ...btn, color: '#fff', background: '#F29213', borderColor: '#F29213' };
const btnDanger = { ...btn, color: '#B91C1C', borderColor: '#FCA5A5' };
const th = { textAlign: 'left', padding: '10px 12px', fontSize: 12, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.02em', borderBottom: '1.5px solid #E5E7EB' };
const td = { padding: '10px 12px', fontSize: 13, color: '#374151', borderBottom: '1px solid #F3F4F6', verticalAlign: 'top' };

const PAGE_SIZE = 20;

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const errorText = (err, fallback) => err.response?.data?.message || err.response?.data?.error || fallback;

export default function RemovedProductsManagement() {
  const confirm = useConfirm();
  const { addToast } = useToast();
  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/products/removed', { params: { page, size: PAGE_SIZE, search: appliedSearch || undefined } })
      .then(r => {
        setProducts(r.data.content || []);
        setTotalPages(r.data.totalPages || 0);
        setTotalElements(r.data.totalElements || 0);
      })
      .catch(() => { setProducts([]); setTotalPages(0); setTotalElements(0); addToast('Could not load removed products.', 'error'); })
      .finally(() => setLoading(false));
  }, [page, appliedSearch, addToast]);

  useEffect(() => { load(); }, [load]);

  // Debounced search; a new search starts from the first page.
  useEffect(() => {
    const t = setTimeout(() => { setPage(0); setAppliedSearch(search.trim()); }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const restore = async (product) => {
    const ok = await confirm({
      title: `Restore "${product.name}"?`,
      message: product.statusBeforeRemoval === 'APPROVED' || !product.statusBeforeRemoval
        ? 'It goes back to the status it had before it was removed. If it was approved, it shows on the shop again.'
        : 'It goes back to the status it had before it was removed.',
      confirmLabel: 'Restore',
      cancelLabel: 'Keep removed',
    });
    if (!ok) return;
    setBusyId(product.id);
    try {
      const r = await api.put(`/products/${product.id}/restore`);
      const status = r.data?.approvalStatus;
      addToast(status === 'APPROVED'
        ? `"${product.name}" is back on the shop.`
        : `"${product.name}" restored — it's waiting for approval.`, 'success');
      load();
    } catch (err) {
      addToast(errorText(err, 'Could not restore the product.'), 'error');
    } finally {
      setBusyId(null);
    }
  };

  // Check first, so staff learn why a product can't go before they type a reason.
  const askDelete = async (product) => {
    setBusyId(product.id);
    try {
      const r = await api.get(`/products/${product.id}/usage`);
      if (r.data?.deletable) {
        setToDelete(product);
      } else {
        await confirm({
          title: `"${product.name}" can't be deleted`,
          message: `It's used in ${(r.data?.uses || []).join(', ')}. It stays removed from the shop, and those keep it — nothing more to do.`,
          confirmLabel: 'Got it',
          cancelLabel: 'Close',
        });
      }
    } catch (err) {
      addToast(errorText(err, 'Could not check where the product is used.'), 'error');
    } finally {
      setBusyId(null);
    }
  };

  const deleteForGood = async (reason) => {
    const product = toDelete;
    setToDelete(null);
    if (!product) return;
    setBusyId(product.id);
    try {
      await api.delete(`/products/${product.id}/permanent`, { params: { reason } });
      addToast(`"${product.name}" deleted.`, 'success');
      // The last row on a later page: step back so the page isn't empty.
      if (products.length === 1 && page > 0) setPage(page - 1); else load();
    } catch (err) {
      addToast(errorText(err, 'Could not delete the product.'), 'error');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div style={{ padding: '24px 0' }}>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: '#111827', margin: '0 0 4px', letterSpacing: '-0.5px' }}>Removed Products</h1>
        <p style={{ fontSize: 13, color: '#6B7280', margin: 0, maxWidth: 720 }}>
          Taken off the shop with “Remove”. Customers can't see or buy them, but curated carts, quotes and orders
          that already have them keep them unchanged. Restore puts one back; only products nothing has ever
          used can be deleted for good.
        </p>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <input
          type="search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by name or ESIN"
          aria-label="Search removed products"
          style={{ padding: '8px 12px', border: '1.5px solid #E5E7EB', borderRadius: 8, fontSize: 13, minWidth: 260 }}
        />
        {!loading && <span style={{ fontSize: 12, color: '#9CA3AF' }}>{totalElements} removed</span>}
      </div>

      {loading ? (
        <p style={{ color: '#9CA3AF' }}>Loading…</p>
      ) : products.length === 0 ? (
        <p style={{ color: '#9CA3AF' }}>{appliedSearch ? 'No removed products match your search.' : 'No products have been removed from the shop.'}</p>
      ) : (
        <div style={{ overflowX: 'auto', background: '#fff', border: '1.5px solid #E5E7EB', borderRadius: 10 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>Product</th>
                <th style={th}>Removed</th>
                <th style={th}>Reason</th>
                <th style={{ ...th, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map(p => (
                <tr key={p.id}>
                  <td style={td}>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                      {(p.thumbnailUrl || p.imageUrl) && (
                        <img src={p.thumbnailUrl || p.imageUrl} alt="" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }} />
                      )}
                      <div>
                        <div style={{ fontWeight: 600, color: '#111827' }}>{p.name}</div>
                        <div style={{ fontSize: 12, color: '#9CA3AF' }}>ESIN {p.id}{p.brand ? ` · ${p.brand}` : ''}</div>
                      </div>
                    </div>
                  </td>
                  <td style={td}>
                    <div>{formatDate(p.removedAt)}</div>
                    <div style={{ fontSize: 12, color: '#9CA3AF' }}>{p.removedBy || '—'}</div>
                  </td>
                  <td style={{ ...td, maxWidth: 320 }}>{p.removedReason || '—'}</td>
                  <td style={{ ...td, textAlign: 'right' }}>
                    <div style={{ display: 'inline-flex', gap: 8 }}>
                      <button type="button" style={btnPrimary} disabled={busyId === p.id} onClick={() => restore(p)}>Restore</button>
                      <button type="button" style={btnDanger} disabled={busyId === p.id} onClick={() => askDelete(p)}>Delete permanently</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />}

      <DeleteConfirmationModal
        isOpen={!!toDelete}
        onClose={() => setToDelete(null)}
        onConfirm={deleteForGood}
        productName={toDelete?.name}
        title="Delete for good"
        message={<>Nothing uses <strong>{toDelete?.name}</strong>, so it can be deleted. This can't be undone.</>}
        reasonLabel="Reason"
        confirmLabel="Delete for good"
      />
    </div>
  );
}
